import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { BidType, MarketProgram } from '@/features/bidding/constants'
import { GET_BID_UPDATE_OVERVIEW_API_ID, getBidUpdateOverview } from '@/features/bidding/endpoints/bids'
import { useErrorHandler } from '@/features/bidding/hooks/useErrorHandler'
import type { BidUpdateOverviewItem } from '@/features/bidding/types/bidUpdate'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import type { UseQueryOptions } from '@/types/queries'

type BidUpdateOverviewQueryParams = {
  portfolioCode?: string
  marketProgram?: MarketProgram
  deliveryDay?: MarketDate
  bidType: BidType
}

export type UseBidUpdateOverviewQueryResult = Omit<UseQueryResult, 'data'> & {
  bidUpdateOverview?: BidUpdateOverviewItem[]
}

export function useBidUpdateOverviewQuery(
  params: BidUpdateOverviewQueryParams,
  options?: UseQueryOptions<BidUpdateOverviewItem[]>,
): UseBidUpdateOverviewQueryResult {
  const { data, ...queryResult } = useQuery({
    ...options,
    queryKey: [
      GET_BID_UPDATE_OVERVIEW_API_ID,
      params.portfolioCode,
      params.marketProgram,
      params.deliveryDay?.toISODate(),
      params.bidType,
    ],
    queryFn: () =>
      getBidUpdateOverview(params.portfolioCode!, params.marketProgram!, params.deliveryDay!, params.bidType),
  })

  useErrorHandler(queryResult.isError)

  return { bidUpdateOverview: data, ...queryResult }
}

export function useInvalidateBidUpdateOverviewQuery() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: [GET_BID_UPDATE_OVERVIEW_API_ID] })
}
